// Karta komentarzy do całej oferty z perspektywy klienta.
// Klient widzi własne komentarze + komentarze handlowca oznaczone isVisibleForClient=true.
// Może dodać własny komentarz, ale nie edytuje cudzych.

import React, { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, CircularProgress, TextField } from '@mui/material';
import { useQueryClient } from 'react-query';

import {
  getSalesCrmMyOfferCommentsQueryKey,
  useGetSalesCrmMyOfferComments,
  usePostSalesCrmMyOfferComment
} from 'api';

interface IProps {
  offerId: string;
  readOnly?: boolean;
}

const formatDate = (raw: string | null): string => {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const ClientOfferCommentsCard: FC<IProps> = ({ offerId, readOnly = false }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [newComment, setNewComment] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data, isLoading, isError } = useGetSalesCrmMyOfferComments(offerId);

  const { mutate: addComment, isLoading: isPosting } = usePostSalesCrmMyOfferComment(offerId, {
    onSuccess: () => {
      setNewComment('');
      setErrorMessage(null);
      queryClient.invalidateQueries(getSalesCrmMyOfferCommentsQueryKey(offerId));
    },
    onError: (e) => setErrorMessage(e?.message ?? t('Nie udało się dodać komentarza.'))
  });

  const handleSubmit = () => {
    const trimmed = newComment.trim();
    if (trimmed === '') {
      setErrorMessage(t('Wprowadź treść komentarza.'));
      return;
    }
    addComment({ comment: trimmed });
  };

  const items = data?.items ?? [];

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #eef0f4',
        borderRadius: 12,
        boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
        overflow: 'hidden'
      }}>
      <div
        style={{
          padding: '16px 20px',
          borderBottom: '1px solid #eef0f4',
          background: '#fafbfc',
          fontSize: 15,
          fontWeight: 700,
          color: '#1a1d29'
        }}>
        {t('Komentarze do oferty')}
      </div>
      <div style={{ padding: 20 }}>

      {isLoading && (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <CircularProgress size={20} />
        </div>
      )}

      {!isLoading && isError && (
        <div style={{ color: '#b3142d' }}>{t('Nie udało się pobrać komentarzy.')}</div>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <div style={{ color: '#4b556a', marginBottom: 16 }}>{t('Brak komentarzy.')}</div>
      )}

      {!isLoading && !isError && items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
          {items.map((c) => (
            <div
              key={c.id}
              style={{
                border: '1px solid #eef0f4',
                borderRadius: 6,
                padding: 12,
                background: c.authorType === 'CLIENT' ? '#f5f3ff' : '#fafbfc'
              }}>
              <div style={{ fontSize: 12, color: '#4b556a', marginBottom: 6 }}>
                {c.authorType === 'CLIENT' ? t('Klient') : t('Handlowiec')}
                {' • '}
                {formatDate(c.createdAt)}
              </div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{c.comment}</div>
              {c.fileUrl && c.fileName && (
                <div style={{ marginTop: 8 }}>
                  <a href={c.fileUrl} target="_blank" rel="noopener noreferrer">
                    {c.fileName}
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!readOnly && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <TextField
            label={t('Nowy komentarz')}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            disabled={isPosting}
          />
          {errorMessage && <div style={{ color: '#b3142d', fontSize: 13 }}>{errorMessage}</div>}
          <div style={{ textAlign: 'right' }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isPosting}
              sx={{ backgroundColor: '#6b54ff', '&:hover': { backgroundColor: '#5a47e0' } }}>
              {isPosting ? <CircularProgress size={18} /> : t('Dodaj komentarz')}
            </Button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default ClientOfferCommentsCard;
